// OrganizationAddInList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout1 from "../../DataLayouts/Layout1DataGrid";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { canSwitchOrganization } from "../../../Utils/organizationPermissions";
import { getOrganizationBySerach } from "../../../Apis/Organization-apis";
import { setClientPreferredOrganizationId } from "../../../Utils/clientActiveOrganization";
import toast from "react-hot-toast";
import { Button } from "@mui/material";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import debounce from "lodash.debounce";
import {
  ORGANIZATION_DELETE_TITLE,
  ORGANIZATION_DELETE_WARNING,
} from "../../../Utils/organizationDeleteWarning";

function resolveClientId(userData) {
  return (
    userData?.organization?.client_id ??
    userData?.client_id ??
    userData?.organizations?.[0]?.client_id ??
    null
  );
}

function OrganizationAddInList() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { userData, login } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const canSwitchOrg = canSwitchOrganization(Permission);
  const org = userData?.organization;
  const clientId = resolveClientId(userData);

  const handleStatusChange = async (id) => {
    if (!canSwitchOrg) return;
    try {
      const response = await axios.post(
        `${MAIN_URL}/api/organizations/extra/active`,
        {
          organization_id: id,
          application_user_id: userData.application_user_id,
        },
        { headers: getApiHeaders() }
      );

      const { user } = response.data;
      const activeOrgId =
        user?.user_active_organization?.organization_id ??
        user?.user_active_organization?.user_current_active_organization_id ??
        id;
      const newActiveOrg = (user?.organization || []).find(
        (item) => Number(item.organization_id) === Number(activeOrgId)
      );
      const mergedOrgUser =
        user?.organizationuser ||
        (Array.isArray(user?.metadata)
          ? user.metadata.find(
              (item) => Number(item?.organization_id) === Number(activeOrgId)
            )
          : null);

      setClientPreferredOrganizationId(id);

      login({
        ...user,
        organizations: Array.isArray(user?.organization) ? user.organization : [],
        organization: newActiveOrg ?? user?.organization?.[0] ?? null,
        organizationuser: mergedOrgUser ?? user?.organizationuser ?? null,
        user_active_organization:
          user?.user_active_organization ?? user?.activeOrganization ?? null,
      });
      toast.success("Status updated successfully!");
      await fetchOrganizations(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.error || "Status update failed.");
    }
  };

  const fetchOrganizations = useCallback(
    async (loadingVal = true, searchQuery = search) => {
      if (!clientId) {
        setOrganizations([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      setLoading(loadingVal);
      try {
        const data = await getOrganizationBySerach(clientId, searchQuery, {
          page: page + 1,
          per_page: perPage,
        });
        const rows = data?.organizations?.data || [];

        setOrganizations(
          rows.map((item) => ({
            ...item,
            id: item.organization_id,
          }))
        );
        setTotalCount(data?.organizations?.total ?? rows.length);
      } catch (err) {
        if (err.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
          return;
        }
        toast.error(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to fetch organizations"
        );
      } finally {
        setLoading(false);
      }
    },
    [clientId, search, page, perPage]
  );

  const debouncedFetch = useMemo(
    () =>
      debounce((val) => {
        fetchOrganizations(true, val);
      }, 500),
    [fetchOrganizations]
  );

  useEffect(() => {
    if (clientId && search !== "") {
      debouncedFetch(search);
    }
    return () => {
      debouncedFetch.cancel();
    };
  }, [search, clientId, debouncedFetch]);

  useEffect(() => {
    if (clientId) {
      fetchOrganizations(true);
    }
  }, [clientId, page, perPage, fetchOrganizations]);

  const tableColumns = useMemo(() => {
    const cols = [
      {
        field: "organization_name",
        headerName: "Organization Name",
        flex: 1,
      },
      {
        field: "organization_short_name",
        headerName: "Short Name",
        flex: 1,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        renderCell: (params) => {
          const isActive =
            Number(org?.organization_id) ===
            Number(params.row.organization_id);
          return (
            <div
              style={{
                fontWeight: "bold",
                color: isActive ? "green" : "red",
              }}
            >
              {isActive ? "ACTIVE" : "INACTIVE"}
            </div>
          );
        },
      },
    ];

    if (canSwitchOrg) {
      cols.push({
        field: "Actions",
        headerName: "Actions",
        flex: 1,
        renderCell: (params) => {
          const isActive =
            Number(org?.organization_id) ===
            Number(params.row.organization_id);

          return (
            <Button
              variant="contained"
              color={isActive ? "error" : "success"}
              size="small"
              startIcon={isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
              onClick={() => handleStatusChange(params.row.organization_id)}
              sx={{
                fontWeight: "bold",
                borderRadius: "8px",
                textTransform: "none",
              }}
            >
              {isActive ? "Inactivate" : "Activate"}
            </Button>
          );
        },
      });
    }

    return cols;
  }, [canSwitchOrg, org?.organization_id]);

  const deleteOrganization = async (id) => {
    try {
      const response = await axios.delete(
        `${MAIN_URL}/api/organizations/${id}`,
        { headers: getApiHeaders() }
      );
      if (response.status === 200) {
        toast.success(
          response.data?.message ||
            "Organization and all related data deleted successfully."
        );
        fetchOrganizations(false);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
        return;
      }
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to delete organization"
      );
      throw error;
    }
  };

  return (
    <Layout1
      loading={loading}
      heading={"Organizations"}
      btnName={"Add Organization"}
      Data={organizations}
      totalCount={totalCount}
      page={page}
      perPage={perPage}
      setPage={setPage}
      setPerPage={setPerPage}
      search={search}
      setSearch={setSearch}
      showActions={true}
      showAddButton={true}
      add_action="ORGANIZATION_ADD"
      edit_action="ORGANIZATION_EDIT"
      delete_action="ORGANIZATION_DELETE"
      Route={"/organization/details"}
      messages={["Organizations", "Organizations", "Add Organization", "Organization"]}
      DeleteFunc={deleteOrganization}
      fetchData={fetchOrganizations}
      deleteConfirmTitle={ORGANIZATION_DELETE_TITLE}
      deleteConfirmMessage={ORGANIZATION_DELETE_WARNING}
      columns={tableColumns}
    />
  );
}

export default OrganizationAddInList;
